import { DIALOG_EXIT_ANIMATION_MS } from '@/shared/config/dialog';
import { type AnyDialogComponent, getDialogComponent } from '@/shared/lib/dialogRegistry';
import { useDialogStore } from '@/shared/stores/dialogStore';
import { useEffect } from 'react';

/**
 * The single dialog surface, mounted once at the app root beside the toast
 * surface. Renders every open instance from {@link useDialogStore} by looking up
 * its component in the registry, injecting the controlled `open`/`onOpenChange`
 * and spreading the caller's typed props. Supports a stack (multiple concurrent
 * dialogs). Unknown ids render nothing — `openDialog` is typed to registered
 * ids, so this only guards a transient open-before-register race.
 */
export function DialogHost() {
  const dialogs = useDialogStore((s) => s.dialogs);
  return (
    <>
      {dialogs.map((inst) => {
        const Component = getDialogComponent(inst.id);
        if (!Component) return null;
        return (
          <DialogInstance
            key={inst.instanceId}
            instanceId={inst.instanceId}
            open={inst.open}
            props={inst.props}
            Component={Component}
          />
        );
      })}
    </>
  );
}

function DialogInstance({
  instanceId,
  open,
  props,
  Component,
}: {
  instanceId: string;
  open: boolean;
  props: unknown;
  Component: AnyDialogComponent;
}) {
  const dismissTop = useDialogStore((s) => s.dismissTop);
  const remove = useDialogStore((s) => s.remove);

  // Two-phase close: `onOpenChange(false)`/`closeDialog` flips `open` to false so
  // the primitive plays its exit animation; remove the instance once it finishes.
  useEffect(() => {
    if (open) return;
    const timer = setTimeout(() => remove(instanceId), DIALOG_EXIT_ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [open, instanceId, remove]);

  const merged: Record<string, unknown> = {
    ...(props as Record<string, unknown>),
    open,
    onOpenChange: (next: boolean) => {
      if (!next) dismissTop(instanceId);
    },
  };
  return <Component {...merged} />;
}
